import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, Zap } from "lucide-react";
import { PAGE_NAME } from "../../types";
import emitter, { EmitterEvents } from "../utils/eventsEmitter";
import { RewardedAdResult } from "../utils/PiNetworkAdsManager";
import { TURBO_ADS_REQUIRED } from "../utils/rewardTables";

const VISIBLE_MS = 2500;

/**
 * Global feedback for rewarded ads: every verified ad emits ADS_REWARDED, so
 * placements without their own success UI (Home timer, listing card, gates)
 * still tell the user the ad paid something.
 */
const AdRewardToast: React.FC = () => {
  const location = useLocation();
  const [toast, setToast] = useState<RewardedAdResult | null>(null);
  const hideTimer = useRef<NodeJS.Timeout | null>(null);

  // The Ad Hub shows its own reward animation — no double feedback there.
  const onAdHub = location.pathname === PAGE_NAME.WATCH_AND_EARN;

  useEffect(() => {
    const onRewarded = (result?: RewardedAdResult) => {
      if (!result) return;
      setToast(result);
      if (hideTimer.current) clearTimeout(hideTimer.current);
      hideTimer.current = setTimeout(() => setToast(null), VISIBLE_MS);
    };
    emitter.addListener(EmitterEvents.ADS_REWARDED, onRewarded);
    return () => {
      emitter.removeListener(EmitterEvents.ADS_REWARDED, onRewarded);
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, []);

  const turbo = !!toast?.turboActivated;

  return (
    <AnimatePresence>
      {toast && !onAdHub && (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ type: "spring", stiffness: 400, damping: 30 }}
          className="fixed bottom-24 inset-x-0 z-[1000] flex justify-center px-4 pointer-events-none"
        >
          <div
            className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl shadow-xl text-white text-sm font-black ${
              turbo
                ? "bg-gradient-to-r from-amber-500 to-orange-500"
                : "bg-gradient-to-r from-emerald-500 to-teal-500"
            }`}
          >
            {turbo ? (
              <>
                <Zap size={16} fill="currentColor" />
                TURBO ACTIVATED — 5x grants for 1h
              </>
            ) : (
              <>
                <CheckCircle2 size={16} />
                Ad rewarded · Turbo {toast.turboCount}/{TURBO_ADS_REQUIRED}
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AdRewardToast;
